import React, { useState, useCallback, useMemo } from 'react';
import { Grid, IconButton, Typography } from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';

import { Title } from '~/components/Title';
import { Table } from '~/components';
import { allocateOpportunityForContractor, sendToManualAllocationAPI } from '~/services/api';
import { OpportunityResponse, OpportunityStatusRequest } from '~/types';
import { useOpportunities } from '~/services/hooks';
import { AttendanceDetailCard } from './components/AttendanceDetailCard';
import Snackbar, { SeverityType } from '~/components/Snackbar';

/** Item da lista de atendimentos pendentes de aceite (de/para a partir de OpportunityResponse) */
interface PendingAttendanceItem {
  id: string;
  number: string;
  rideValue: string;
  providerId?: string;
  customerName: string;
  serviceDescription: string;
}

function mapOpportunityToPendingItem(opp: OpportunityResponse): PendingAttendanceItem {
  const rideValue =
    typeof opp.total === 'number'
      ? opp.total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '0,00';
  return {
    id: opp.id,
    number: opp.number ?? opp.id,
    rideValue,
    providerId: opp.contractor_id || undefined,
    customerName: opp.customer_name || '—',
    serviceDescription: opp.service_description || '—',
  };
}

export interface AcceptAttendanceProps {
  embedded?: boolean;
}

export default function AcceptAttendance({ embedded = false }: AcceptAttendanceProps) {
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [loadingAccept, setLoadingAccept] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [messageSnack, setMessageSnack] = useState('');
  const [severity, setSeverity] = useState<SeverityType>('info');

  const { data: opportunities, loading: loadingList, invalidateQuery } = useOpportunities(
    OpportunityStatusRequest.MANUAL_ALLOCATION,
    null,
    null,
  );

  const pendingList = useMemo(
    () => (opportunities ?? []).map(mapOpportunityToPendingItem),
    [opportunities],
  );

  const selectedOpportunity = useMemo(
    () => opportunities?.find(o => o.id === selectedOrderId),
    [opportunities, selectedOrderId],
  );

  const openDetail = useCallback((orderId: string) => {
    setSelectedOrderId(orderId);
    setView('detail');
  }, []);

  const backToList = useCallback(() => {
    setView('list');
    setSelectedOrderId(null);
  }, []);

  const handleAccept = useCallback(
    async (contractorId: string) => {
      if (!selectedOrderId) return;
      setLoadingAccept(true);
      try {
        await allocateOpportunityForContractor(selectedOrderId, contractorId);
        setMessageSnack('Atendimento aceito com sucesso');
        setSeverity('success');
        setOpenSnackbar(true);
        invalidateQuery();
        backToList();
      } catch (err: any) {
        setMessageSnack(err?.response?.data?.message || 'Erro ao aceitar atendimento');
        setSeverity('error');
        setOpenSnackbar(true);
      } finally {
        setLoadingAccept(false);
      }
    },
    [selectedOrderId, backToList, invalidateQuery],
  );

  const handleReject = useCallback(async () => {
    if (!selectedOrderId) return;
    setLoadingAccept(true);
    try {
      await sendToManualAllocationAPI(selectedOrderId, {
        reallocate: false,
        cancellation_reason: 'BACKOFFICE - Recusado pelo painel de aceite',
      });
      setMessageSnack('Atendimento recusado');
      setSeverity('success');
      setOpenSnackbar(true);
      invalidateQuery();
      backToList();
    } catch (err: any) {
      setMessageSnack(err?.response?.data?.message || 'Erro ao recusar atendimento');
      setSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setLoadingAccept(false);
    }
  }, [selectedOrderId, backToList, invalidateQuery]);

  if (view === 'detail' && selectedOrderId && selectedOpportunity) {
    const rideValueFormatted =
      typeof selectedOpportunity.total === 'number'
        ? selectedOpportunity.total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '0,00';
    return (
      <>
        <Grid container alignItems="center" style={{ marginBottom: 16 }}>
          <IconButton onClick={backToList} size="small">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" style={{ marginLeft: 8 }}>Aceitar atendimento</Typography>
        </Grid>
        <Grid container>
          <AttendanceDetailCard
            orderId={selectedOrderId}
            opportunity={selectedOpportunity}
            rideValue={rideValueFormatted}
            providerId={selectedOpportunity.contractor_id || undefined}
            onAccept={handleAccept}
            onReject={handleReject}
            loading={loadingAccept}
          />
        </Grid>
        <Snackbar message={messageSnack} open={openSnackbar} severity={severity} onClose={() => setOpenSnackbar(false)} />
      </>
    );
  }

  return (
    <>
      {!embedded && <Title marginBottom={20}>Aceitar atendimento</Title>}
      <p style={{ color: '#6a6c6e', marginBottom: 16 }}>
        Novos atendimentos aparecem aqui. Clique em uma linha para abrir e aceitar, recusar ou direcionar para outro
        prestador.
      </p>
      <Table<PendingAttendanceItem>
        columns={{
          number: 'ID do pedido',
          rideValue: {
            title: 'Valor',
            render: row => (row.rideValue ? `R$ ${row.rideValue}` : '—'),
          },
          customerName: { title: 'Cliente' },
          serviceDescription: { title: 'Serviço' },
          actions: {
            title: 'Ações',
            filtering: false,
            sorting: false,
            render: row => (
              <button type="button" onClick={() => openDetail(row.id)}>
                Abrir
              </button>
            ),
          },
        }}
        data={pendingList}
        title=""
        options={{
          search: true,
          paging: true,
          actionsColumnIndex: -1,
        }}
        isLoading={loadingList}
      />
      {!loadingList && pendingList.length === 0 && (
        <p style={{ color: '#6a6c6e', marginTop: 16 }}>Nenhum atendimento pendente de aceite.</p>
      )}
      <Snackbar message={messageSnack} open={openSnackbar} severity={severity} onClose={() => setOpenSnackbar(false)} />
    </>
  );
}
